import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from '@/components/ui/dropdown-menu'
import { CreditCard, ArrowRight, Calendar, ThumbsUp, Banknote, RotateCcw } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/utils'

const STATUS_CONFIG = {
  aberto: { bg: 'bg-slate-50', badge: 'bg-slate-200 text-slate-700', label: 'Aberto' },
  pronto_pagamento: { bg: 'bg-amber-50', badge: 'bg-amber-200 text-amber-800', label: 'Pronto' },
  programado: { bg: 'bg-purple-50', badge: 'bg-purple-200 text-purple-800', label: 'Programado' },
  aprovado: { bg: 'bg-blue-50', badge: 'bg-blue-200 text-blue-800', label: 'Aprovado' },
  pago: { bg: 'bg-green-50', badge: 'bg-green-200 text-green-800', label: 'Pago' }
}

export default function ParcelasDialog({
  open,
  onOpenChange,
  processo,
  onProntoPagamento,
  onProgramar,
  onAprovar,
  onPagar,
  onRetroceder,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-teal-600" />
            Gerenciar Parcelas
          </DialogTitle>
          <DialogDescription>
            {processo && (
              <span>
                Processo: <strong>{processo.numero_processo}</strong> - {processo.fornecedor}
                <br />
                Valor Total: <strong>{formatCurrency(processo.valor)}</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {processo?.parcelas && processo.parcelas.length > 0 ? (
          <div className="space-y-2">
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">Parc.</th>
                    <th className="px-2 py-1.5 text-right font-medium text-slate-600">Valor</th>
                    <th className="px-2 py-1.5 text-center font-medium text-slate-600">Status</th>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">Fonte</th>
                    <th className="px-2 py-1.5 text-center font-medium text-slate-600">Previsão</th>
                    <th className="px-2 py-1.5 text-center font-medium text-slate-600">Pago em</th>
                    <th className="px-2 py-1.5 text-center font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {processo.parcelas.map((parcela) => {
                    const config = STATUS_CONFIG[parcela.status] || STATUS_CONFIG.aberto
                    return (
                      <tr key={parcela.numero} className={`${config.bg} border-b border-slate-100`}>
                        <td className="px-2 py-1.5 font-medium">{parcela.numero}/{processo.numero_parcelas}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-teal-700">{formatCurrency(parcela.valor)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                            {config.label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-xs">{parcela.fonte_recurso || '-'}</td>
                        <td className="px-2 py-1.5 text-center text-xs">{parcela.previsao_pagamento ? formatDate(parcela.previsao_pagamento) : '-'}</td>
                        <td className="px-2 py-1.5 text-center text-xs">{parcela.data_pagamento ? formatDate(parcela.data_pagamento) : '-'}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center justify-center gap-1">
                            {parcela.status === 'aberto' && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-amber-700 hover:bg-amber-100" onClick={() => onProntoPagamento(parcela.numero)} title="Pronto p/ Pagamento" data-testid={`btn-parc-pronto-${parcela.numero}`}>
                                <ArrowRight className="w-3 h-3" />
                              </Button>
                            )}
                            {parcela.status === 'pronto_pagamento' && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-purple-700 hover:bg-purple-100" onClick={() => onProgramar(parcela.numero)} title="Programar" data-testid={`btn-parc-programar-${parcela.numero}`}>
                                <Calendar className="w-3 h-3" />
                              </Button>
                            )}
                            {(parcela.status === 'pronto_pagamento' || parcela.status === 'programado') && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-700 hover:bg-blue-100" onClick={() => onAprovar(parcela.numero)} title="Aprovar" data-testid={`btn-parc-aprovar-${parcela.numero}`}>
                                <ThumbsUp className="w-3 h-3" />
                              </Button>
                            )}
                            {parcela.status === 'aprovado' && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-green-700 hover:bg-green-100" onClick={() => onPagar(parcela.numero)} title="Pagar" data-testid={`btn-parc-pagar-${parcela.numero}`}>
                                <Banknote className="w-3 h-3" />
                              </Button>
                            )}
                            {parcela.status !== 'aberto' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-500">
                                    <RotateCcw className="w-3 h-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="text-xs">
                                  <DropdownMenuLabel className="text-xs py-1">Retroceder</DropdownMenuLabel>
                                  <DropdownMenuItem className="text-xs py-1" onClick={() => onRetroceder(parcela.numero, 'aberto')}>
                                    Aberto
                                  </DropdownMenuItem>
                                  {(parcela.status === 'programado' || parcela.status === 'aprovado' || parcela.status === 'pago') && (
                                    <DropdownMenuItem className="text-xs py-1" onClick={() => onRetroceder(parcela.numero, 'pronto_pagamento')}>
                                      Pronto p/ Pgto
                                    </DropdownMenuItem>
                                  )}
                                  {(parcela.status === 'aprovado' || parcela.status === 'pago') && (
                                    <DropdownMenuItem className="text-xs py-1" onClick={() => onRetroceder(parcela.numero, 'programado')}>
                                      Programado
                                    </DropdownMenuItem>
                                  )}
                                  {parcela.status === 'pago' && (
                                    <DropdownMenuItem className="text-xs py-1" onClick={() => onRetroceder(parcela.numero, 'aprovado')}>
                                      Aprovado
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-4 gap-2 p-2 bg-slate-100 rounded text-xs">
              <div className="text-center">
                <div className="font-semibold text-slate-700">{processo.parcelas.filter(p => p.status === 'pago').length}/{processo.numero_parcelas}</div>
                <div className="text-slate-500">Pagas</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-green-700">{formatCurrency(processo.parcelas.filter(p => p.status === 'pago').reduce((sum, p) => sum + p.valor, 0))}</div>
                <div className="text-slate-500">Valor Pago</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-amber-700">{formatCurrency(processo.parcelas.filter(p => p.status !== 'pago').reduce((sum, p) => sum + p.valor, 0))}</div>
                <div className="text-slate-500">Pendente</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-teal-700">{formatCurrency(processo.valor)}</div>
                <div className="text-slate-500">Total</div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-500 py-4">Este processo não possui parcelas</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
